<template>
  <div class="account-pill">
    <div class="avatar" :style="avatarStyle">{{ initial }}</div>
    <b class="who">{{ displayName }}</b>
    <div class="pill-actions">
      <button
        class="iconbtn pill-action pill-refresh"
        :class="{ 'is-spinning': isSyncing || isRefreshing }"
        :disabled="isSyncing"
        :aria-label="syncTitle"
        :title="syncTitle"
        @click="doRefresh"
      >
        <Icon name="refresh" :size="13" />
      </button>
      <button
        class="iconbtn pill-action"
        aria-label="Open settings"
        title="Settings"
        @click="openSettings"
      >
        <Icon name="gear" :size="13" />
      </button>
      <button
        class="iconbtn pill-action"
        aria-label="Sign out"
        title="Sign out"
        :disabled="signingOut"
        @click="signOut"
      >
        <Icon name="logout" :size="13" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useIsFetching, useQueryClient } from '@tanstack/vue-query'
import { globalState } from '@/stores/globalState'
import { useAuth } from '@/composables/useAuth'
import { useTransactionSync } from '@/composables/useTransactionSync'
import Icon from './Icon.vue'

const router = useRouter()
const auth = useAuth()
const signingOut = ref(false)

// Manual bank refresh. It sits here — beside Settings and Sign out — rather
// than in a top bar; the narration it produces is rendered by the mobile top
// bar from the same shared state.
const queryClient = useQueryClient()
const { isSyncing, syncTitle, refresh } = useTransactionSync()
// Any TanStack query in flight also spins the icon, so background refetches
// are visible without a second indicator.
const fetchingCount = useIsFetching()
const isRefreshing = computed(() => fetchingCount.value > 0)
function doRefresh() { refresh(auth as any, queryClient) }

// Sign out via the Auth0 SDK — clears app state, the query cache, and the
// Auth0 session, then returns to the landing page.
async function signOut() {
  signingOut.value = true
  try {
    await auth.logoutUser()
  } catch (err) {
    console.error('[AccountPill] sign out failed:', err)
    signingOut.value = false
  }
}

const displayName = computed(() => globalState.displayName || globalState.username || 'Account')

const initial = computed(() => displayName.value.charAt(0).toUpperCase())

// Deterministic avatar tint from the username so the pill feels personalised
// without storing colour preferences.
const avatarStyle = computed(() => {
  const src = displayName.value
  let hash = 0
  for (let i = 0; i < src.length; i++) hash = (hash * 31 + src.charCodeAt(i)) | 0
  const cat = (Math.abs(hash) % 8) + 1
  return { background: `var(--cat-${cat})`, color: 'var(--bg-elev)' }
})

function openSettings() {
  router.push({ name: 'Settings' }).catch(() => { /* route may not exist yet during cutover */ })
}
</script>

<style scoped>
.account-pill {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}
/* flex-shrink: 0 — without it the row's overflow (a long name) is shared out
   between the avatar and the name by width, narrowing the circle into an oval. */
.account-pill .avatar {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 600;
  font-size: 11px;
}
.account-pill .who {
  min-width: 0;
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Three actions in a 220px column: they share one tight group so the name
   keeps as much room as possible. */
.pill-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.pill-action {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

/* Refresh spins while a sync (or any background refetch) is in flight. */
.pill-refresh.is-spinning :deep(.icon) {
  animation: pill-refresh-spin 0.8s linear infinite;
  transform-origin: 50% 50%;
}
.pill-refresh:disabled { cursor: default; }
@keyframes pill-refresh-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .pill-refresh.is-spinning :deep(.icon) { animation: none; }
}

/* Drawer mode: refresh / settings / sign-out grow to tappable size. */
@media (max-width: 880px) {
  .account-pill { padding: 10px 12px; }
  .pill-actions { gap: 4px; }
  .pill-action {
    width: 40px;
    height: 40px;
    border-radius: 9px;
  }
  .pill-action .icon { width: 16px; height: 16px; }
}
</style>
